<template>
  <div>
    <page-header
      has-return
      left-side-class="col-8"
      right-side-class="col-4 d-flex align-items-center"
      :page="headerConfig"
      :actions="headerActionsConfig"
    />
    <board v-if="studentDetails" :details="studentDetails" class="mb-4 mt-2" />
    <section-title has-line content="Sección Demográfica" class="mt-4 pt-2" />
    <div class="mx-4">
      <demographic-info
        ref="demographicInfo"
        v-model="demographicModel"
        :languages="languages"
        :municipalities="municipalities"
        :read-only="isReadOnly"
      ></demographic-info>
      <QuestionAnswer
        ref="questionsInfo"
        v-model="questionModel"
        :read-only="isReadOnly"
        :student-grade="studentGrade"
      />
      <parents-authorization
        ref="parentsAuthorizationInfo"
        :data="parentsAuthorization"
        @file-change="onFileChange"
        @file-remove="onFileRemove"
        @file-download="onFileDownload"
        :read-only="isReadOnly"
      ></parents-authorization>
    </div>
    <div class="row text-right pb-5">
      <div class="col">
        <action-button
          v-if="!isReadOnly"
          class="mt-md-4"
          variant="success"
          i="fas fa-flag"
          text="Someter"
          @click="save(true, true)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import mixAlert from "@/mixin/alert.mixin";
import storeMixin from "@/mixin/storeGenerator";

import PageHeader from "PageHeader";
import Board from "Board";
import SectionTitle from "SectionTitle";
import DemographicInfo from "./components/DemographicInfo.vue";
import QuestionAnswer from "./components/QuestionAnswer.vue";
import ParentsAuthorization from "./components/ParentsAuthorization.vue";

import locationServices from "@/services/api/location.api";
import {
  getVisualImpairmentDetails,
  saveVisualImpairment
} from "@/services/studentVisualImpairments.js";
import { getLanguagesValidForREIV } from "@/modules/common/code.api";
import { visualImpairmentStatuses } from "@/utils/constants/student/visualImpairmentsStatus";

import { date } from "@/utils/filters.js";
import { Other as OTHER_LANG } from "@/utils/constants/codeLanguages.js";
import objectToFormData from "object-to-formdata";
import documentService from "@/services/documents";
import ActionButton from "ActionButton";

export default {
  name: "studentsRegistrationVisualInpairmentsEnterRecord",
  mixins: [mixAlert, storeMixin],
  components: {
    PageHeader,
    Board,
    SectionTitle,
    DemographicInfo,
    QuestionAnswer,
    ParentsAuthorization,
    ActionButton
  },
  data() {
    return {
      student: null,
      statusName: null,
      demographicModel: {},
      questionModel: {
        questionAnswers: [],
        otherQuestionAnswers: [],
        gradeId: null,
        differentGradeReason: null
      },
      documentModel: {
        file: null,
        deletedDocumentId: null
      },
      parentsAuthorization: {},
      municipalities: [],
      languages: [],
      statusId: null,
      isEdenSubmitted: false
    };
  },
  async mounted() {
    Promise.all([
      this.getImpairmentDetails(),
      this.getMunicipalities(),
      this.geLanguages()
    ]);
  },
  computed: {
    headerConfig() {
      return {
        icon: "fas fa-user-circle",
        title: "Registro de Estudiantes con Impedimentos Visuales",
        routes: [
          "Inicio",
          "Estudiantes",
          "Registro de Estudiantes con Impedimentos Visuales"
        ]
      };
    },
    headerActionsConfig() {
      return [
        {
          text: "Someter",
          icon: "fas fa-flag mr-1",
          variant: "outline-success",
          visible: !this.isReadOnly,
          click: () => this.save(true, true)
        },
        {
          text: "Guardar",
          icon: "fas fa-save mr-1",
          variant: "success",
          visible: !this.isReadOnly,
          click: () => this.save()
        }
      ];
    },
    studentDetails() {
      if (!this.student) return;
      return [
        [
          {
            label: "Estudiante",
            data: `${this.student.fullName} - ${this.student.sieId}`,
            icon: "fas fa-address-book",
            cols: "col-12 col-md-4"
          },
          {
            label: "Edad",
            data: this.studentAge,
            icon: "fas fa-file-spreadsheet",
            cols: "col-12 col-md-4"
          },
          {
            label: "Fecha de Nacimiento",
            data: date(this.student.dob),
            icon: "fas fa-calendar-day",
            cols: "col-12 col-md-4"
          },
          {
            label: "Estatus",
            data: this.statusName,
            icon: "fas fa-check-circle",
            cols: "col-12 col-md-4"
          },
          {
            label: "Impedimento",
            data: this.student.impedimentName,
            icon: "fas fa-clipboard-list-check",
            cols: "col-12 col-md-4"
          },
          {
            label: "Escuela",
            data: this.student.school,
            icon: "fas fa-school",
            cols: "col-12 col-md-4"
          }
        ]
      ];
    },
    impairmentId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null;
    },
    studentAge() {
      return `${this.student.age}`.includes(".")
        ? `${this.student.age}`.split(".")[0]
        : null;
    },
    studentGrade() {
      return this.student?.gradeId;
    },
    isReadOnly() {
      if (this.isEdenSubmitted) return true;
      const validStatuses = [
        visualImpairmentStatuses.Submitted,
        visualImpairmentStatuses.Cancelled
      ];
      return validStatuses.includes(this.statusId);
    }
  },
  methods: {
    async getImpairmentDetails() {
      const { data } = await getVisualImpairmentDetails(this.impairmentId);
      const {
        studentInfo,
        demographicInfo,
        questionAnswer,
        parentsAuthorization,
        isEdenSubmitted
      } = data;
      this.isEdenSubmitted = isEdenSubmitted;
      this.statusId = data.statusId;
      this.statusName = data.statusName;
      this.student = studentInfo;
      this.demographicModel = demographicInfo;
      this.questionModel = {
        gradeId: questionAnswer.gradeId,
        differentGradeReason: questionAnswer.differentGradeReason,
        questionAnswers: questionAnswer.answers
          .filter(x => x.answerId && x.answer === null)
          .map(x => ({
            questionId: x.questionId,
            answerId: x.answerId,
            answer: x.answer
          })),
        otherQuestionAnswers: questionAnswer.answers
          .filter(x => x.answer?.length > 0)
          .map(x => ({
            questionId: x.questionId,
            answerId: x.answerId,
            answer: x.answer
          }))
      };
      this.parentsAuthorization = parentsAuthorization;
    },
    async getMunicipalities() {
      const academicYearId = this.currentAcademicYear()?.id ?? 0;
      const { data } = await locationServices.getMunicipalities({
        academicYearId,
        skipUserCentersFlag: true,
        skipUserRegionsFlag: true,
        skipUserDistrictsFlag: true,
        skipUserMunicipalitiesFlag: true,
        skipUserSchoolCodesFlag: true,
        skipUserSchoolsFlag: true
      });
      this.municipalities = data;
    },
    async geLanguages() {
      const { data } = await getLanguagesValidForREIV();
      this.languages = data;
      this.languages.push(OTHER_LANG);
    },
    async save(validate = false, changeTosubmitted = false) {
      if (validate) {
        const isDemographicFormValid = await this.$refs.demographicInfo.$refs.form.validate();
        const isQuestionsFormValid = await this.$refs.questionsInfo.$refs.form.validate();
        const isSAEEFormValid = await this.$refs.parentsAuthorizationInfo.validate();

        if (
          !isDemographicFormValid ||
          !isQuestionsFormValid ||
          !isSAEEFormValid
        ) {
          this.ShowToast(
            "Error",
            "Favor completar los campos requeridos para poder someter",
            "error"
          );
          return;
        }
      }

      try {
        const payload = {
          demographic: this.demographicModel,
          questionAnswer: {
            answers: [
              ...this.questionModel.questionAnswers,
              ...this.questionModel.otherQuestionAnswers
            ].map(x => ({
              questionId: x.questionId,
              answerId: x.answerId,
              answer: x.answer
            })),
            gradeId: this.questionModel.gradeId,
            differentGradeReason: this.questionModel.differentGradeReason
          },
          ...this.documentModel,
          isSubmitted: changeTosubmitted
        };
        if (payload.demographic.languageId === OTHER_LANG.id)
          payload.demographic.languageId = null;
        const formData = objectToFormData(payload, { indices: true });
        await saveVisualImpairment(this.impairmentId, formData);
        this.ShowToast(
          "",
          "La información ha sido guardada exitosamente",
          "success"
        );
        Promise.all([this.getImpairmentDetails(), this.geLanguages()]);
      } catch (error) {
        this.ShowToast(
          "Error",
          error?.response?.data?.message ?? error?.message,
          "error"
        );
      }
    },
    onFileChange(file) {
      this.documentModel.file = file;
      this.parentsAuthorization.documentName = file.name;
    },
    onFileRemove({ documentId }) {
      this.documentModel.deletedDocumentId = documentId;
    },
    async onFileDownload({ documentId }) {
      await documentService.downloadFromServer(documentId);
    }
  }
};
</script>

<style scoped lang="scss"></style>
